import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Crosshair, ExternalLink, Heart, Trophy, DollarSign, Sparkles } from "lucide-react";
import Navigation from "../components/Navigation";
import SpeakerButton from "../components/SpeakerButton";
import ThreatMap from "../components/sentinel/ThreatMap";
import BountyCard from "../components/sentinel/BountyCard";
import HuntModal from "../components/sentinel/HuntModal";
import {
  fetchCadet, fetchStreak,
  fetchBounties, claimSolana,
  fetchSentinelLeaderboard, fetchThreatmap, fetchSentinelEarnings, fetchDonationPool
} from "../lib/api";

const TABS = [
  { id: "sandbox", label: "EIGOO Sandbox", icon: ShieldCheck, accent: "from-red-500 to-red-700" },
  { id: "solana",  label: "Solana Strike", icon: Crosshair,    accent: "from-cyan-500 to-cyan-700" },
];

export const Sentinel = () => {
  const [cadet, setCadet] = useState(null);
  const [streak, setStreak] = useState({ streak_days: 0 });
  const [tab, setTab] = useState("sandbox");
  const [bounties, setBounties] = useState([]);
  const [threatmap, setThreatmap] = useState({ points: [] });
  const [leaderboard, setLeaderboard] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [donations, setDonations] = useState({ total_usd: 0 });
  const [hunt, setHunt] = useState(null);

  const refreshAll = async () => {
    const [bs, lb, em, dn, tm] = await Promise.all([
      fetchBounties(tab),
      fetchSentinelLeaderboard(tab),
      fetchSentinelEarnings(),
      fetchDonationPool(),
      fetchThreatmap(),
    ]);
    setBounties(bs);
    setLeaderboard(lb);
    setEarnings(em);
    setDonations(dn);
    setThreatmap(tm);
  };

  useEffect(() => {
    (async () => {
      setCadet(await fetchCadet().catch(() => null));
      setStreak(await fetchStreak().catch(() => ({ streak_days: 0 })));
    })();
  }, []);

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const onAccepted = async () => {
    await refreshAll();
    setCadet(await fetchCadet().catch(() => null));
  };

  const onClaim = async (bounty) => {
    try {
      await claimSolana(bounty.id, "");
      window.open(bounty.external_url, "_blank", "noopener,noreferrer");
      await refreshAll();
    } catch (e) {
      alert("Claim failed");
    }
  };

  const totalBounties = bounties.length;
  const openCount = useMemo(() => bounties.filter(b => b.my_status === "open").length, [bounties]);

  return (
    <div className="min-h-screen bg-black text-white">
      <Navigation cadet={cadet} streakDays={streak.streak_days || 0} />

      {/* Hero */}
      <div className="relative border-b border-yellow-300/15 overflow-hidden">
        <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
          background: "radial-gradient(900px 400px at 20% -10%, rgba(239,68,68,0.18), transparent 60%), radial-gradient(700px 300px at 90% 0%, rgba(34,211,238,0.15), transparent 60%)"
        }} />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-14">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div>
              <div className="text-[10px] md:text-xs tracking-[0.4em] uppercase text-red-300/80 font-display">
                Sovereign Sentinel · Bug Bounty Vault
              </div>
              <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl mt-2 leading-tight">
                Hunt the <span className="text-red-400">flaw.</span> Earn the <span className="text-yellow-300">tribute.</span>
              </h1>
              <p className="mt-3 max-w-xl text-white/70 text-sm md:text-base">
                Every accepted finding splits <span className="text-yellow-300 font-bold">70 / 20 / 10</span> — Hunter, Sovereign Treasury,
                CyberKids Donation Pool. Your eyes feed the next cadet's tuition.
              </p>
              <div className="mt-3 flex items-center gap-2">
                <SpeakerButton text="Welcome to the Sovereign Sentinel Vault. Hunt the flaw. Earn the tribute. Every find feeds the next cadet." testid="sentinel-speaker" size="sm" />
                <span className="text-xs text-white/40">Hear the briefing</span>
              </div>
            </div>

            {/* Donation pool counter */}
            <motion.div
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl border border-cyan-400/30 bg-cyan-500/5 px-5 py-4 min-w-[260px]"
              data-testid="donation-pool-counter"
            >
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-cyan-300/80 font-display">
                <Heart size={12} /> CyberKids Donation Pool
              </div>
              <div className="font-display font-black text-3xl text-cyan-200 mt-1">
                ${donations.total_usd.toFixed(2)}
              </div>
              <div className="text-[11px] text-cyan-100/60 mt-1">
                Total donated by Hunters · funds the program
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 space-y-8">
        {/* Threat Map */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-black text-lg uppercase tracking-widest text-yellow-200">Live Threat Map</h2>
            <div className="text-[11px] text-white/45 font-mono">{threatmap.points.length} active vectors</div>
          </div>
          <ThreatMap points={threatmap.points} />
        </section>

        {/* Tabs */}
        <section>
          <div className="flex items-center gap-2 p-1 rounded-2xl bg-white/5 border border-yellow-300/15 w-fit" data-testid="sentinel-tabs">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`sentinel-tab-${t.id}`}
                  className={`px-4 py-2 rounded-xl font-display font-black text-sm flex items-center gap-2 transition ${active ? `bg-gradient-to-br ${t.accent} text-white shadow-lg` : "text-white/70 hover:text-white"}`}
                >
                  <Icon size={16} /> {t.label}
                </button>
              );
            })}
          </div>
          <div className="mt-2 text-xs text-white/50">
            {tab === "sandbox"
              ? `${openCount} open · ${totalBounties - openCount} closed by you · internal hunts grade your findings instantly.`
              : `${totalBounties} curated external bounties · click "Claim Intel" to log the pursuit + open the platform in a new tab.`}
          </div>
        </section>

        {/* Bounties grid */}
        <section className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="bounties-grid">
          {bounties.map((b) => (
            <BountyCard key={b.id} bounty={b} onHunt={setHunt} onClaim={onClaim} />
          ))}
          {bounties.length === 0 && (
            <div className="col-span-full text-center text-white/45 py-10 text-sm">No bounties in this track.</div>
          )}
        </section>

        {/* Leaderboard + Earnings */}
        <section className="grid md:grid-cols-3 gap-5">
          <div className="md:col-span-2 rounded-2xl border border-yellow-300/15 bg-zinc-950 p-5" data-testid="sentinel-leaderboard">
            <div className="flex items-center gap-2 mb-3">
              <Trophy size={16} className="text-yellow-300" />
              <h3 className="font-display font-black uppercase tracking-widest text-yellow-200 text-sm">
                Top Hunters · {tab === "sandbox" ? "Sandbox" : "Solana Strike"}
              </h3>
            </div>
            {leaderboard.length === 0 ? (
              <div className="text-sm text-white/45 py-6 text-center">No accepted bounties yet — be the first to plant a flag.</div>
            ) : (
              <div className="space-y-2">
                {leaderboard.map((row, i) => (
                  <div key={row.cadet_id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/3 border border-white/8">
                    <div className={`h-8 w-8 rounded-lg flex items-center justify-center font-display font-black text-sm ${i === 0 ? "bg-yellow-300 text-black" : i === 1 ? "bg-zinc-300 text-black" : i === 2 ? "bg-orange-400 text-black" : "bg-white/10 text-white/70"}`}>
                      {i + 1}
                    </div>
                    <div className="flex-1">
                      <div className="font-display font-bold text-white">{row.callsign}</div>
                      <div className="text-[11px] uppercase tracking-widest text-white/40">{row.rank} · {row.bounties} hunts</div>
                    </div>
                    <div className="font-mono font-black text-yellow-200">${row.total_usd.toFixed(2)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-yellow-300/15 bg-zinc-950 p-5" data-testid="sentinel-earnings">
            <div className="flex items-center gap-2 mb-3">
              <DollarSign size={16} className="text-yellow-300" />
              <h3 className="font-display font-black uppercase tracking-widest text-yellow-200 text-sm">My Ledger</h3>
            </div>
            {earnings ? (
              <>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-white/60">Hunter (70%)</span><span className="font-mono font-bold text-yellow-200">${earnings.buckets.hunter.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-white/60">Treasury (20%)</span><span className="font-mono font-bold text-red-300">${earnings.buckets.treasury.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-white/60">Donation (10%)</span><span className="font-mono font-bold text-cyan-300">${earnings.buckets.donation.toFixed(2)}</span></div>
                </div>
                <div className="mt-3 pt-3 border-t border-white/10 grid grid-cols-2 gap-2 text-center">
                  <div className="rounded-lg bg-white/5 p-2">
                    <div className="text-[10px] uppercase tracking-widest text-white/45">Accepted</div>
                    <div className="font-display font-black text-xl text-emerald-300">{earnings.accepted_count}</div>
                  </div>
                  <div className="rounded-lg bg-white/5 p-2">
                    <div className="text-[10px] uppercase tracking-widest text-white/45">Claimed</div>
                    <div className="font-display font-black text-xl text-cyan-300">{earnings.claimed_count}</div>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-sm text-white/45 py-4">Loading…</div>
            )}
          </div>
        </section>

        <div className="text-center py-4 text-[11px] text-white/40 italic flex items-center justify-center gap-2">
          <Sparkles size={12} className="text-yellow-300" />
          Every flaw you find feeds the next cadet's tuition.
        </div>
      </div>

      {hunt && <HuntModal bounty={hunt} onClose={() => setHunt(null)} onAccepted={onAccepted} />}
    </div>
  );
};

export default Sentinel;
